/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { Button, FontFaces, FormPanel, Spinner } from '@micromag/core/components';
import { useUrlGenerator } from '@micromag/core/contexts';
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { useParsedStory } from '@micromag/core/hooks';
import { ScreensProvider } from '@micromag/screens';

import { useStory } from '../../../hooks';
import useAnalyticsOrganisationSummary from '../../../hooks/useAnalyticsOrganisationSummary';
import useAnalyticsStorySummary from '../../../hooks/useAnalyticsStorySummary';
import useAnalyticsScreenSummary from '../../../hooks/useAnalyticsScreenSummary';
import useAnalyticsScreenEvents from '../../../hooks/useAnalyticsScreenEvents';
import { useOrganisation } from '../../../contexts/OrganisationContext';

import MainLayout from '../../layouts/Main';
import Page from '../../partials/Page';
import OrganisationMenu from '../../menus/Organisation';
import Summary from '../../lists/Summary';
import AllStories from '../../partials/AllStories';
import ScreensList from '../../lists/Screens';
import AnalyticsStartDate from '../../partials/AnalyticsStartDate';
import TrackingEventsList from '../../lists/TrackingEvents';

// import { tmpScreenEvents } from '../../../lib/getFormattedTrackingEvents';

const propTypes = {
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const StatsPage = ({ className }) => {
    const [pathname] = useLocation();
    const url = useUrlGenerator();

    const title = <FormattedMessage defaultMessage="Stats" descrition="Page title" />;
    const startDates = [
        {
            name: '7daysAgo',
            label: <FormattedMessage defaultMessage="Last week" descrition="Button label" />,
        },
        {
            name: '30daysAgo',
            label: <FormattedMessage defaultMessage="Last month" descrition="Button label" />,
        },
        {
            name: '1yearAgo',
            label: <FormattedMessage defaultMessage="Last year" descrition="Button label" />,
        },
    ];

    const nav = [{ label: title, url: pathname }];

    const params = useParams();

    const { story: storyId = null, screen: screenId = null } = params;

    // startDate: week / month / year
    const firstStartDate = startDates.length > 0 ? startDates[0] : null;
    const { name: firstStartDateName = null } = firstStartDate || {};
    const [startDate, setStartDate] = useState(firstStartDateName);
    const query = useMemo(() => ({ start_date: startDate }), [startDate]);
    const onStartDateClick = useCallback(
        (startDateName) => {
            setStartDate(startDateName);
        },
        [setStartDate],
    );

    // organisation summary
    const organisation = useOrganisation();
    const { name: organisationName = null } = organisation || {};
    const orgStats = useAnalyticsOrganisationSummary(query);
    const { summary: organisationSummary = null, loading: organisationLoading = false } =
        orgStats || {};

    // story
    const withStory = storyId !== null;
    const { story = null } = useStory(storyId, { autoload: withStory });
    const hasStory = story !== null;
    const { title: storyTitle = null } = story || {};

    // story summary
    const storyStats = useAnalyticsStorySummary(storyId, query, { autoload: withStory });
    const { summary: storySummary = null, loading: storyLoading = false } = storyStats || {};
    const { components: screens = [], fonts = null } = useParsedStory(story) || {};
    const finalFonts = useMemo(() => fonts || [], [fonts]);

    // screen
    const withScreen = screenId !== null;
    const screenIndex = screens.findIndex(({ id }) => id === screenId);
    const screen = screenIndex > -1 ? screens[screenIndex] : null;
    const { parameters: screenParameters } = screen || {};
    const { metadata: screenMetadata } = screenParameters || {};
    const { title: screenTitle = null } = screenMetadata || {};
    const hasScreen = screen !== null;

    // screen summary
    const screenStats = useAnalyticsScreenSummary(storyId, screenId, query, {
        autoload: withScreen,
    });
    const { summary: screenSummary = null, loading: screenLoading = false } = screenStats || {};

    // screen events
    // eslint-disable-next-line
    const screenEvents = useAnalyticsScreenEvents(storyId, screenId, query, {
        autoload: withScreen,
    });

    let loading = true;
    let backButton = null;
    let pageTitle = null;
    let pageSummary = null;
    let pageContent = null;

    if (!withStory) {
        loading = organisationLoading || organisationSummary === null;
        pageTitle = organisationName;
        pageSummary = organisationSummary;
        pageContent = (
            <AllStories route="organisations.stats" itemLink="organisations.stats.story" asList />
        );
    } else if (!withScreen) {
        loading = storyLoading || storySummary === null || !hasStory;
        backButton = {
            link: url('organisations.stats'),
            label: (
                <FormattedMessage
                    defaultMessage="Back to stories"
                    description="Back to stories button label"
                />
            ),
        };
        pageTitle = storyTitle;
        pageSummary = storySummary;
        pageContent = (
            <>
                <FontFaces fonts={finalFonts} />
                <ScreensList
                    items={screens}
                    itemLink="organisations.stats.screen"
                    story={story}
                    path={pathname}
                />
            </>
        );
    } else {
        loading = screenLoading || screenSummary === null || !hasScreen;
        pageTitle = (
            <>
                <FormattedMessage defaultMessage="Screen" description="Screen" />
                {hasScreen ? ` ${screenIndex + 1} (${screen.type})` : null}
                {screenTitle !== null ? ` - ${screenTitle}` : null}
            </>
        );
        backButton = {
            link: url('organisations.stats.story', {
                story: storyId,
            }),
            label: (
                <FormattedMessage
                    defaultMessage="Back to screens"
                    description="Back to screens button label"
                />
            ),
        };
        pageSummary = screenSummary;
        // TODO: plug the real events
        pageContent = <TrackingEventsList items={[]} />;
    }

    return (
        <MainLayout nav={nav}>
            <Page
                title={title}
                sidebar={<OrganisationMenu asList />}
                className={classNames([
                    {
                        [className]: className !== null,
                    },
                ])}
            >
                <ScreensProvider>
                    <FormPanel>
                        <div className="d-flex mb-3">
                            {backButton !== null ? (
                                <Button
                                    theme="secondary"
                                    href={backButton.link}
                                    icon={<FontAwesomeIcon icon={faAngleLeft} />}
                                >
                                    {backButton.label}
                                </Button>
                            ) : null}
                            <AnalyticsStartDate
                                className="ms-auto"
                                startDates={startDates}
                                selectedName={startDate}
                                onItemClick={onStartDateClick}
                            />
                        </div>
                        <h3 className="mb-3">{pageTitle}</h3>
                        {loading ? (
                            <Spinner />
                        ) : (
                            <>
                                <Summary className="mb-5" summary={pageSummary} />
                                {pageContent}
                            </>
                        )}
                    </FormPanel>
                </ScreensProvider>
            </Page>
        </MainLayout>
    );
};

StatsPage.propTypes = propTypes;
StatsPage.defaultProps = defaultProps;

export default StatsPage;
